import {RegistrationStatusStatus as RegistrationStatusV3} from '@wix/ambassador-events-v3-event/types'
import {RegistrationStatus, VisitorType} from '@wix/events-types'
import {type RichContent, isRichContentEmpty} from '@wix/ricos'
import {
  hasGuestsGoing as commonsHasGuestsGoing,
  getAboutText,
  getEventRichContent,
  getRegistrationStatus,
  getRestrictedTo,
  hasSeatingPlan,
  isEventLocationOnline,
  isLocationTbd,
  isRegistrationClosed,
  isRegistrationClosedVisible,
  isRegistrationScheduled,
  isRsvp,
  isWithoutRegistration,
  ticketedOrInitiallyTicketed,
} from '@wix/wix-events-commons-statics'
import {isEditor} from '../../../../commons/selectors/environment'
import {State, ThankYouMessageState} from '../types/state'
import {isMemberRsvpExists} from './member-rsvp'
import {areSelectedDonationsValid, getSelectedTicketsQuantity} from './selected-tickets'
import {areAllTicketsSoldOut, getTickets, hasTicketsOnSale} from './tickets'

const DEFAULT_GUEST_COUNT = 3

export const getEvent = (state: State) => state.event.event

export const getDefaultGuestTotal = (rsvpEvent: boolean): TotalEventGuests =>
  rsvpEvent ? {YES: DEFAULT_GUEST_COUNT} : DEFAULT_GUEST_COUNT

export const getFormMessages = (event: wix.events.Event, state: ThankYouMessageState) => {
  const {negativeMessages, waitlistMessages, positiveMessages} = event.form.messages.rsvp

  switch (state) {
    case 'no':
      return negativeMessages
    case 'waiting':
      return waitlistMessages
    default:
      return positiveMessages
  }
}

export const hasGuestsGoing = (state: State) => {
  const event = getEvent(state)
  const totalGuests = event?.totalGuests ?? 0
  const guestsGoing = commonsHasGuestsGoing(totalGuests, isRsvp(event))

  if (typeof totalGuests === 'object') {
    return Object.keys(totalGuests)?.length > 0 && guestsGoing
  }

  return Boolean(totalGuests) && guestsGoing
}

export const getTotalGuests = (state: State): TotalEventGuests => {
  const event = getEvent(state)
  const rsvpEvent = isRsvp(event)
  const areGuestsGoing = hasGuestsGoing(state)

  if (!areGuestsGoing && isEditor(state)) {
    return getDefaultGuestTotal(rsvpEvent)
  }

  return event.totalGuests
}

export const getMembers = (state: State): Member[] => getEvent(state).members || []

export const isAboutSectionVisible = (state: State) => {
  const event = getEvent(state)
  const richContent = getEventRichContent(event) as unknown as RichContent

  return isEditor(state) || !!getAboutText(event) || !!(richContent && !isRichContentEmpty(richContent))
}

export const isMapVisible = (state: State) => !isLocationTbd(getEvent(state)) && !isEventLocationOnline(getEvent(state))

export const isSecondRegistrationButtonVisible = (state: State) => {
  const event = getEvent(state)
  const registrationClosed = isRegistrationClosed(event)
  const ticketedEvent = ticketedOrInitiallyTicketed(event)
  const withoutRegistration = isWithoutRegistration(event)
  const memberRsvpExists = isMemberRsvpExists(state)
  const registrationScheduled = isRegistrationScheduled(event)

  return !(registrationClosed || memberRsvpExists || ticketedEvent || withoutRegistration || registrationScheduled)
}

export const isDemoEvent = (state: State) => Boolean(Object.keys(state.demoEvents).length)

export const isCheckoutAvailable = (state: State) => {
  const event = getEvent(state)

  return hasSeatingPlan(event)
    ? !isRegistrationClosedVisible(event) && hasTicketsOnSale(getTickets(state))
    : !isRegistrationClosedVisible(event) && getSelectedTicketsQuantity(state) && areSelectedDonationsValid(state)
}

export const isCheckoutButtonDisabled = (state: State) => {
  const event = getEvent(state)
  const tickets = getTickets(state)

  return areAllTicketsSoldOut(tickets) || !hasTicketsOnSale(tickets) || isRegistrationClosedVisible(event)
}

export const isRestrictedTo = (state: State, visitorType: VisitorType) =>
  getRestrictedTo(getEvent(state)) === visitorType

const REGISTRATION_STATUS_MAP: Record<RegistrationStatus, RegistrationStatusV3> = {
  [RegistrationStatus.NA_REGISTRATION_STATUS]: RegistrationStatusV3.UNKNOWN_REGISTRATION_STATUS,
  [RegistrationStatus.CLOSED]: RegistrationStatusV3.CLOSED_AUTOMATICALLY,
  [RegistrationStatus.CLOSED_MANUALLY]: RegistrationStatusV3.CLOSED_MANUALLY,
  [RegistrationStatus.OPEN_RSVP]: RegistrationStatusV3.OPEN_RSVP,
  [RegistrationStatus.OPEN_RSVP_WAITLIST]: RegistrationStatusV3.OPEN_RSVP_WAITLIST_ONLY,
  [RegistrationStatus.OPEN_TICKETS]: RegistrationStatusV3.OPEN_TICKETS,
  [RegistrationStatus.OPEN_EXTERNAL]: RegistrationStatusV3.OPEN_EXTERNAL,
  [RegistrationStatus.SCHEDULED_RSVP]: RegistrationStatusV3.SCHEDULED_RSVP,
  [RegistrationStatus.OPEN_RSVP_AND_TICKETS]: RegistrationStatusV3.OPEN_RSVP_AND_TICKETS,
} as const

export const getRegistrationStatusV3 = (state: State) => REGISTRATION_STATUS_MAP[getRegistrationStatus(getEvent(state))]
